import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_URL } from "../../config/apiConfig";
import {
  FaWallet,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaSync,
  FaPhone,
  FaEnvelope,
} from "react-icons/fa";
import { toast } from "react-toastify";

const UserWithdrawals = () => {
  const [withdrawals, setWithdrawals] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [referenceId, setReferenceId] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [processingId, setProcessingId] = useState(null);

  const token = localStorage.getItem("token");

  useEffect(() => {
    fetchWithdrawals();
  }, [currentPage]);

  const fetchWithdrawals = async () => {
    try {
      setIsLoading(true);
      const config = {
        headers: { Authorization: `Bearer ${token}` },
      };
      const response = await axios.get(
        `${API_URL}/wallet/admin/user-withdrawals/pending?page=${currentPage}&limit=10`,
        config
      );

      setWithdrawals(response.data.data.withdrawals || []);
      setTotalPages(response.data.data.pagination.pages);
    } catch (error) {
      console.error("Error fetching withdrawals:", error);
      toast.error("Failed to fetch pending withdrawals");
    } finally {
      setIsLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!referenceId.trim()) {
      toast.error("Please enter a reference ID");
      return;
    }

    try {
      setProcessingId(selectedWithdrawal._id);
      const config = {
        headers: { Authorization: `Bearer ${token}` },
      };

      await axios.post(
        `${API_URL}/wallet/admin/user-withdrawals/approve`,
        {
          withdrawalId: selectedWithdrawal._id,
          referenceId,
          adminId: JSON.parse(localStorage.getItem("user"))?._id || "",
        },
        config
      );

      toast.success("Withdrawal approved successfully!");
      setShowApprovalModal(false);
      setReferenceId("");
      fetchWithdrawals();
    } catch (error) {
      console.error("Error approving withdrawal:", error);
      toast.error(
        error.response?.data?.message || "Failed to approve withdrawal"
      );
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error("Please enter a reason for rejection");
      return;
    }

    try {
      setProcessingId(selectedWithdrawal._id);
      const config = {
        headers: { Authorization: `Bearer ${token}` },
      };

      await axios.post(
        `${API_URL}/wallet/admin/user-withdrawals/reject`,
        {
          withdrawalId: selectedWithdrawal._id,
          reason: rejectReason,
          adminId: JSON.parse(localStorage.getItem("user"))?._id || "",
        },
        config
      );

      toast.success("Withdrawal rejected successfully!");
      setShowRejectModal(false);
      setRejectReason("");
      fetchWithdrawals();
    } catch (error) {
      console.error("Error rejecting withdrawal:", error);
      toast.error(
        error.response?.data?.message || "Failed to reject withdrawal"
      );
    } finally {
      setProcessingId(null);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="w-full px-2 sm:px-4 md:px-6 lg:px-8 py-0">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 flex items-center gap-3">
            <FaWallet className="text-blue-600" />
            User Withdrawal Requests
          </h1>
          <p className="text-gray-600 mt-2">
            Manage and approve/reject user wallet withdrawal requests
          </p>
        </div>

        {/* Stats Card */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="grid grid-cols-3 gap-4">
            <div className="border-l-4 border-yellow-500 pl-4">
              <p className="text-gray-600 text-sm">Pending Requests</p>
              <p className="text-2xl font-bold text-yellow-600">
                {withdrawals.length}
              </p>
            </div>
            <div className="border-l-4 border-blue-500 pl-4">
              <p className="text-gray-600 text-sm">Total Pending Amount</p>
              <p className="text-2xl font-bold text-blue-600">
                {formatCurrency(
                  withdrawals.reduce((sum, w) => sum + (w.amount || 0), 0)
                )}
              </p>
            </div>
            <div className="border-l-4 border-green-500 pl-4">
              <button
                onClick={() => {
                  setCurrentPage(1);
                  fetchWithdrawals();
                }}
                className="text-green-600 hover:text-green-700 flex items-center gap-2"
              >
                <FaSync /> Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Withdrawals Table */}
        {isLoading ? (
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <FaSync className="animate-spin text-blue-600 text-2xl mx-auto mb-4" />
            <p className="text-gray-600">Loading pending withdrawals...</p>
          </div>
        ) : withdrawals.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <FaCheckCircle className="text-green-600 text-6xl mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-800 mb-2">
              No Pending Withdrawals
            </h3>
            <p className="text-gray-600">All user withdrawal requests have been processed!</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-100 border-b">
                  <tr>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">
                      User Details
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">
                      Bank Details
                    </th>
                    <th className="px-6 py-3 text-right text-sm font-semibold text-gray-900">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">
                      Request Date
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {withdrawals.map((withdrawal) => (
                    <tr key={withdrawal._id} className="border-b hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-semibold text-gray-900">
                            {withdrawal.userId?.name || "N/A"}
                          </p>
                          <div className="flex items-center gap-2 text-gray-600 text-sm mt-1">
                            <FaPhone className="text-xs" />
                            {withdrawal.userId?.phone || "N/A"}
                          </div>
                          <div className="flex items-center gap-2 text-gray-600 text-sm">
                            <FaEnvelope className="text-xs" />
                            {withdrawal.userId?.email || "N/A"}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        <p className="font-semibold">
                          {withdrawal.userId?.bankDetails?.bankName || "N/A"}
                        </p>
                        <p className="text-gray-600 text-xs">
                          {withdrawal.userId?.bankDetails?.accountNumber?.slice(-4).padStart(
                            withdrawal.userId?.bankDetails?.accountNumber?.length,
                            "*"
                          ) || "N/A"}
                        </p>
                        <p className="text-gray-600 text-xs">
                          {withdrawal.userId?.bankDetails?.ifscCode || "N/A"}
                        </p>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <p className="text-xl font-bold text-red-600">
                          -{formatCurrency(withdrawal.amount)}
                        </p>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        <div className="flex items-center gap-2">
                          <FaClock className="text-yellow-600" />
                          {formatDate(withdrawal.createdAt)}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              setSelectedWithdrawal(withdrawal);
                              setShowApprovalModal(true);
                            }}
                            disabled={processingId === withdrawal._id}
                            className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-4 py-2 rounded text-sm font-semibold flex items-center gap-2 transition"
                          >
                            <FaCheckCircle className="text-sm" />
                            Approve
                          </button>
                          <button
                            onClick={() => {
                              setSelectedWithdrawal(withdrawal);
                              setShowRejectModal(true);
                            }}
                            disabled={processingId === withdrawal._id}
                            className="bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white px-4 py-2 rounded text-sm font-semibold flex items-center gap-2 transition"
                          >
                            <FaTimesCircle className="text-sm" />
                            Reject
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-t">
                <button
                  onClick={() =>
                    setCurrentPage(Math.max(1, currentPage - 1))
                  }
                  disabled={currentPage === 1}
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 disabled:bg-gray-100 text-gray-800 rounded transition"
                >
                  Previous
                </button>
                <span className="text-gray-700 font-semibold">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() =>
                    setCurrentPage(Math.min(totalPages, currentPage + 1))
                  }
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 disabled:bg-gray-100 text-gray-800 rounded transition"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}

      {/* Approval Modal */}
      {showApprovalModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Approve Withdrawal
            </h2>
            <div className="bg-green-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-600 mb-1">Amount</p>
              <p className="text-2xl font-bold text-green-600">
                {formatCurrency(selectedWithdrawal?.amount)}
              </p>
            </div>
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Reference ID (Bank Transaction ID)
              </label>
              <input
                type="text"
                value={referenceId}
                onChange={(e) => setReferenceId(e.target.value)}
                placeholder="e.g., TXN123456789"
                className="w-full border-2 border-gray-300 rounded-lg p-3 focus:outline-none focus:border-green-600"
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowApprovalModal(false);
                  setReferenceId("");
                }}
                className="flex-1 bg-gray-200 text-gray-800 font-bold py-2 rounded-lg hover:bg-gray-300 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleApprove}
                disabled={processingId === selectedWithdrawal?._id}
                className="flex-1 bg-green-600 text-white font-bold py-2 rounded-lg hover:bg-green-700 disabled:bg-gray-400 transition"
              >
                {processingId === selectedWithdrawal?._id ? "Processing..." : "Approve"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Reject Withdrawal
            </h2>
            <div className="bg-red-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-600 mb-1">Amount</p>
              <p className="text-2xl font-bold text-red-600">
                {formatCurrency(selectedWithdrawal?.amount)}
              </p>
              <p className="text-xs text-red-600 mt-2">
                This amount will be refunded to the user's wallet
              </p>
            </div>
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Reason for Rejection
              </label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="e.g., Incorrect bank details provided"
                className="w-full border-2 border-gray-300 rounded-lg p-3 focus:outline-none focus:border-red-600 resize-none"
                rows="4"
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectReason("");
                }}
                className="flex-1 bg-gray-200 text-gray-800 font-bold py-2 rounded-lg hover:bg-gray-300 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={processingId === selectedWithdrawal?._id}
                className="flex-1 bg-red-600 text-white font-bold py-2 rounded-lg hover:bg-red-700 disabled:bg-gray-400 transition"
              >
                {processingId === selectedWithdrawal?._id ? "Processing..." : "Reject"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserWithdrawals;
